import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Grid,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
} from '@chakra-ui/react';
import { CargoType, DocumentType, FreightServiceAddOn } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { confirmationOptions, prettyBoolean } from '@pelicargo/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { TrpcOutputs } from '../config/trpc';
import { useRequestDocuments } from '../hooks/documents/useRequestDocuments';
import { usePersistItnNumber } from '../hooks/shipmentForms/usePersistItnNumber';
import { QuoteBookedModal } from '../screens/QuoteConfirmation/QuoteBookedModal';
import {
  AwbInstructionVariant,
  ShipmentInstructionsModal,
} from '../screens/QuoteConfirmation/ShipmentInstructionsModal';
import { buildAwbDocumentLabels } from '../utils/awbDocumentLabels';
import { AwbInstructionsViewModal } from './AwbInstructionsViewModal';
import { DocumentGroups } from './Documents/DocumentGroups';
import { DocumentsModal } from './Documents/DocumentsModal';
import { LabeledValue } from './LabeledValue';
import { SimpleInput } from './SimpleInput';
import { SimpleSwitch } from './SimpleSwitch';

const aesFilingOptions = [...confirmationOptions, { label: 'Exempt', value: 'exempt' }];

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
  shipmentForms?: TrpcOutputs['getShipmentFormsByRequestId'];
  /**
   * The fulfillment partner gets the same card, but it reads the paperwork rather than filling it in: the forwarder
   * owns the AES answer, the ITN and the AWB instructions, so those are values here and the footer opens the read-only
   * viewer instead of the editable flow.
   */
  variant?: 'forwarder' | 'fulfillment-partner';
};

export const PaperworkCard = ({ quote, shipmentForms, variant = 'forwarder' }: Props) => {
  const isFulfillmentPartner = variant === 'fulfillment-partner';
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
  const [documentsFlow, setDocumentsFlow] = useState<'manage' | 'additional'>('manage');
  const [isShipmentOpen, setIsShipmentOpen] = useState(false);
  const [isBookedModalOpen, setIsBookedModalOpen] = useState(false);
  const [awbInstructionsReady, setAwbInstructionsReady] = useState<boolean | undefined>(undefined);
  const [bookedIsConsolidated, setBookedIsConsolidated] = useState<boolean | undefined>(undefined);
  const [awbInstructionFlow, setAwbInstructionFlow] = useState<AwbInstructionVariant>('mawb-hawb');

  const methods = useForm<{ aesRequested: boolean | 'exempt'; itnNumber: string }>({
    defaultValues: {
      aesRequested: quote?.request?.freight_service_add_ons?.includes(FreightServiceAddOn.AES) || false,
      itnNumber: '',
    },
  });

  const request = quote?.request;
  // Seeded from the quote payload so the card renders instantly, then kept live
  const [documents] = useRequestDocuments(request?.id, request?.documents);

  // The ITN is saved on the MAWB shipment form, so surface it here once the
  // forms load (keeps this field and the AWB Instructions modal in sync).
  const mawbItnNumber = useMemo(
    () => shipmentForms?.find((form) => !form.parent_shipment_form_id)?.itn_number ?? '',
    [shipmentForms],
  );

  useEffect(() => {
    if (mawbItnNumber) methods.setValue('itnNumber', mawbItnNumber);
  }, [mawbItnNumber, methods]);

  const persistItnNumber = usePersistItnNumber({ quoteId: quote?.id, requestId: request?.id, shipmentForms });

  const isDangerousGoods = request?.cargo_type === CargoType.DangerousGoods;

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [
      DocumentType.SLI,
      DocumentType.SHIPPER_DOCUMENTS,
      DocumentType.AIRWAY_BILL,
      DocumentType.BOL,
      DocumentType.LABELS,
    ];

    if (isDangerousGoods) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    return suggested;
  }, [isDangerousGoods]);

  // Airway bills are MAWB/HAWB based on the shipment form they're attached to
  // (filenames are unreliable), so resolve MAWB vs HAWB from that linkage.
  const awbLabelByDocumentId = useMemo(() => buildAwbDocumentLabels(shipmentForms), [shipmentForms]);

  const hawbCount = useMemo(() => {
    const hawbs = documents.filter((doc) => !!doc.id && !!awbLabelByDocumentId[doc.id]?.startsWith('HAWB'));
    return Math.max(1, hawbs.length);
  }, [documents, awbLabelByDocumentId]);
  const shipmentHawbCount = awbInstructionFlow === 'direct' ? 0 : hawbCount;

  const handleOpenAwbInstructions = (flow: AwbInstructionVariant) => {
    setAwbInstructionFlow(flow);
    setIsShipmentOpen(true);
  };

  // Initial answers for the post-booking "Booking Requested" modal. The HAWB
  // answer prefers the one just given on the booking form: the quote we hold here
  // is served from cache while its refetch is in flight, so `is_consolidated` can
  // still be the pre-booking value. Falls back to the server on refresh.
  const aesRequested = !!request?.freight_service_add_ons?.includes(FreightServiceAddOn.AES);
  const aesFilingAnswer = methods.watch('aesRequested') ?? false;
  const showItnNumber = aesFilingAnswer !== 'exempt';
  const hawbsRequired = bookedIsConsolidated ?? !!request?.is_consolidated;

  const handleAesAnswerChange = useCallback(
    (answer: boolean | 'exempt') => methods.setValue('aesRequested', answer),
    [methods],
  );

  // When arriving on the booked page straight after booking, automatically open
  // the post-booking modal that collects paperwork answers and leads into the
  // AWB Instructions flow.
  const location = useLocation();
  const navigate = useNavigate();
  const hasAutoOpenedBookedModal = useRef(false);

  useEffect(() => {
    const state = location.state as {
      openAwbInstructions?: boolean;
      awbInstructionsReady?: boolean;
      isConsolidated?: boolean;
    } | null;
    if (!state?.openAwbInstructions || hasAutoOpenedBookedModal.current) return;

    hasAutoOpenedBookedModal.current = true;
    // Carry the booking form's answers over before the navigation state is cleared.
    setAwbInstructionsReady(state.awbInstructionsReady);
    setBookedIsConsolidated(state.isConsolidated);
    setIsBookedModalOpen(true);
    // Clear the navigation state so the modal doesn't re-open on refresh or back.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  const handleAdditionalDocumentsFromShipment = () => {
    setIsShipmentOpen(false);
    setDocumentsFlow('additional');
    setIsDocumentsOpen(true);
  };

  const handleCloseDocuments = () => {
    setIsDocumentsOpen(false);
    setDocumentsFlow('manage');
  };

  // The "Additional Documents" step is reached from the AWB Instructions modal,
  // so its Back returns there instead of cancelling the whole flow.
  const handleDocumentsBack = () => {
    setIsDocumentsOpen(false);
    setIsShipmentOpen(true);
  };

  return (
    <FormProvider {...methods}>
      <>
        <Card h="fit-content">
          <CardHeader px="4">
            <Heading variant="h5">Paperwork</Heading>
          </CardHeader>
          <CardBody px="4" py="0" pb="4">
            <Stack spacing="4">
              <Show if={isFulfillmentPartner}>
                <Stack spacing="1">
                  <LabeledValue label="AES Filing" value={prettyBoolean(aesRequested)} />
                  <LabeledValue label="ITN Number" value={mawbItnNumber} defaultValue="-" />
                </Stack>
              </Show>
              <Show if={!isFulfillmentPartner}>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="3">
                  <Stack
                    borderWidth="1px"
                    borderColor="border.light"
                    borderRadius="md"
                    px="3"
                    py="2.5"
                    spacing="2"
                    justify="center"
                    minH="88px"
                  >
                    <SimpleSwitch
                      name="aesRequested"
                      label="Do you need us to file AES?"
                      options={aesFilingOptions}
                      direction="row"
                      variant="outline"
                      fieldVariant="unstyled"
                      h="fit-content"
                    />
                  </Stack>

                  <Show if={showItnNumber}>
                    <Stack
                      borderWidth="1px"
                      borderColor="border.light"
                      borderRadius="md"
                      px="3"
                      py="2.5"
                      spacing="2"
                      justify="center"
                      minH="88px"
                    >
                      <SimpleInput
                        name="itnNumber"
                        label="ITN Number (if available)"
                        placeholder="XYYYYMMDD123456"
                        variant="unstyled"
                        // Saved on blur: the AWB Instructions modal is the only other consumer,
                        // and this card can be left without ever opening it.
                        inputProps={{ onBlur: (event) => void persistItnNumber(event.target.value) }}
                      />
                    </Stack>
                  </Show>
                </Grid>
              </Show>

              {/* Every document on the shipment, grouped by whoever uploaded it */}
              <DocumentGroups
                documents={documents}
                requestId={request?.id}
                expectedTypes={documentTypes}
                labelByDocumentId={awbLabelByDocumentId}
                titleVariant={isFulfillmentPartner ? 'strong' : 'subtle'}
              />

              <Show if={!isFulfillmentPartner}>
                <Divider />
              </Show>

              {/* Footer actions */}
              <Stack direction="row" spacing="2">
                <Button flex="1" colorScheme="primary" variant="outline" onClick={() => setIsDocumentsOpen(true)}>
                  Manage Uploads
                </Button>
                <Show if={isFulfillmentPartner}>
                  <AwbInstructionsViewModal
                    shipmentForms={shipmentForms ?? quote?.shipment_forms}
                    dimensionUnit={request?.original_dimension_unit}
                    buttonProps={{ flex: '1', size: 'md', colorScheme: 'primary', variant: 'outline' }}
                  />
                </Show>
                <Show if={!isFulfillmentPartner}>
                  <Box flex="1">
                    <Menu matchWidth>
                      <MenuButton as={Button} w="full" colorScheme="primary" variant="outline">
                        Start AWB Instructions
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleOpenAwbInstructions('direct')}>Direct AWB</MenuItem>
                        <MenuItem onClick={() => handleOpenAwbInstructions('mawb-hawb')}>MAWB with HAWB(s)</MenuItem>
                      </MenuList>
                    </Menu>
                  </Box>
                </Show>
              </Stack>
            </Stack>
          </CardBody>
        </Card>

        <DocumentsModal
          isOpen={isDocumentsOpen}
          onClose={handleCloseDocuments}
          onBack={documentsFlow === 'additional' ? handleDocumentsBack : undefined}
          title={documentsFlow === 'additional' ? 'Additional Documents' : 'Manage Uploads'}
          submitLabel="Update"
          backLabel="Back"
          mode="manage"
          showBack={documentsFlow === 'additional'}
          documentTypes={documentTypes}
          defaultDocuments={documents}
          isApprovable
        />

        {/* Filling in and being walked through the instructions is the forwarder's flow. */}
        <Show if={!isFulfillmentPartner}>
          <ShipmentInstructionsModal
            quote={quote}
            shipmentForms={shipmentForms}
            isOpen={isShipmentOpen}
            flowIsOpen={isShipmentOpen}
            onClose={() => setIsShipmentOpen(false)}
            onBack={() => setIsShipmentOpen(false)}
            onAdditionalDocuments={handleAdditionalDocumentsFromShipment}
            hawbCount={shipmentHawbCount}
            variant={awbInstructionFlow}
            mode="update"
            initialItnNumber={methods.watch('itnNumber')}
          />

          <QuoteBookedModal
            quote={quote}
            shipmentForms={shipmentForms}
            isOpen={isBookedModalOpen}
            onClose={() => setIsBookedModalOpen(false)}
            aesRequested={aesFilingAnswer}
            onAesAnswerChange={handleAesAnswerChange}
            hawbsRequired={hawbsRequired}
            awbInstructionsReady={awbInstructionsReady}
          />
        </Show>
      </>
    </FormProvider>
  );
};
